/**
 * Event access tokens on the donor web (GH-871b, DEC-052).
 *
 * While membership is on, an event's page, its total, its prompts and check-in open to a donor who
 * is not a member only with an event access token for that event, sent in `x-event-access`. A
 * token comes from one of two places:
 *
 *   - the big screen's QR, which lands on `/c/<eventId>?via=display_qr&access=<token>`;
 *   - the at-event location check (`POST /v1/public/events/access-by-location`).
 *
 * ## What is kept, where, and for how long
 *
 * One token per event, in **session storage** (this tab, until it closes), and never longer than
 * the token's own life: the expiry is read from the token's envelope where it parses, else
 * `ACCESS_RULES.eventAccessAcceptSeconds` from now. The envelope is read only to decide when to
 * forget a token and which event it names — never to decide whether it is valid; the server alone
 * does that. Every storage call is wrapped: a private window or a refused storage keeps the tokens
 * in memory for the life of the page, and the page works the same.
 *
 * The token is taken out of the address at once (`history.replaceState`), like `?join=`, so a link
 * copied from this tab or a screenshot of the address bar does not carry it on.
 *
 * Nothing here is sent anywhere by itself. `api.ts` and `donor-session.ts` read
 * `eventAccessHeaders(eventId)` on the four requests the gate decides.
 */
import {
  ACCESS_RULES,
  EVENT_ACCESS_HEADER,
  EVENT_ACCESS_QUERY_PARAM,
  eventAccessTokenSchema,
} from "@givehere/contracts";

const STORAGE_KEY = "givehere.event-access.v1";

type Held = { readonly token: string; readonly expiresAtMs: number };
type HeldMap = Readonly<Record<string, Held>>;

/** Where tokens live when session storage cannot be used. Per page, never shared. */
let memory: HeldMap = {};
/** Set once a write is refused, after which memory is the only truth for this page. */
let storageRefused = false;

function readStored(): HeldMap {
  if (storageRefused) return memory;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (raw === null) return memory;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return memory;
    const out: Record<string, Held> = {};
    for (const [eventId, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof value !== "object" || value === null) continue;
      const { token, expiresAtMs } = value as Record<string, unknown>;
      if (typeof token !== "string" || typeof expiresAtMs !== "number") continue;
      if (!eventAccessTokenSchema.safeParse(token).success) continue;
      out[eventId] = { token, expiresAtMs };
    }
    return out;
  } catch {
    return memory;
  }
}

function writeStored(next: HeldMap): void {
  memory = next;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Storage refused: the in-memory copy above serves this page.
    storageRefused = true;
  }
}

/** Only the tokens still inside their life. */
function live(map: HeldMap, nowMs: number): HeldMap {
  const out: Record<string, Held> = {};
  for (const [eventId, held] of Object.entries(map)) {
    if (held.expiresAtMs > nowMs) out[eventId] = held;
  }
  return out;
}

/**
 * The envelope's event id and expiry, if the token parses — housekeeping only, never trust.
 * `<base64url(JSON {k, e, n, p: {u, v, s, i}})>.<signature>`.
 */
export function readEnvelope(token: string): {
  eventId: string | null;
  expiresAtMs: number | null;
} {
  try {
    const body = token.split(".")[0] ?? "";
    const padded = body.replace(/-/g, "+").replace(/_/g, "/");
    const json: unknown = JSON.parse(
      window.atob(padded + "=".repeat((4 - (padded.length % 4)) % 4)),
    );
    const envelope = json as { e?: unknown; p?: { v?: unknown } };
    return {
      eventId: typeof envelope.p?.v === "string" ? envelope.p.v : null,
      expiresAtMs: typeof envelope.e === "number" ? envelope.e : null,
    };
  } catch {
    return { eventId: null, expiresAtMs: null };
  }
}

/** Keep a token for an event, until `expiresAtMs` (or the token's own expiry, or four hours). */
export function rememberEventAccess(
  eventId: string,
  token: string,
  expiresAt: string | null = null,
  nowMs: number = Date.now(),
): void {
  if (!eventAccessTokenSchema.safeParse(token).success) return;
  const ceiling = nowMs + ACCESS_RULES.eventAccessAcceptSeconds * 1000;
  const stated = expiresAt !== null ? Date.parse(expiresAt) : NaN;
  const envelope = readEnvelope(token).expiresAtMs;
  const expiresAtMs = Math.min(
    ceiling,
    Number.isFinite(stated) ? stated : envelope !== null ? envelope : ceiling,
  );
  if (expiresAtMs <= nowMs) return;
  writeStored({ ...live(readStored(), nowMs), [eventId]: { token, expiresAtMs } });
}

/** The token held for this event, if it is still inside its life. */
export function eventAccessFor(eventId: string, nowMs: number = Date.now()): string | null {
  const held = readStored()[eventId];
  if (!held) return null;
  if (held.expiresAtMs <= nowMs) {
    forgetEventAccess(eventId);
    return null;
  }
  return held.token;
}

/** Drop a token the server refused (expired, or for another event). */
export function forgetEventAccess(eventId: string): void {
  const rest = { ...readStored() };
  delete rest[eventId];
  writeStored(rest);
}

/** The header to send for this event, or nothing. */
export function eventAccessHeaders(eventId: string): Record<string, string> {
  const token = eventAccessFor(eventId);
  return token ? { [EVENT_ACCESS_HEADER]: token } : {};
}

/**
 * Read `?access=` from the address, keep it for the event it names (or `pathEventId`), and take it
 * out of the address bar at once. Returns whether a usable token was kept.
 */
export function takeEventAccessToken(
  pathEventId: string,
  location: Pick<Location, "search" | "pathname" | "hash"> = window.location,
): boolean {
  const query = new URLSearchParams(location.search);
  const raw = query.get(EVENT_ACCESS_QUERY_PARAM);
  if (raw === null) return false;
  query.delete(EVENT_ACCESS_QUERY_PARAM);
  try {
    const rest = query.toString();
    window.history.replaceState(
      window.history.state,
      "",
      `${location.pathname}${rest ? `?${rest}` : ""}${location.hash}`,
    );
  } catch {
    // An address that cannot be rewritten keeps the token in the bar; it lapses on its own.
  }
  const parsed = eventAccessTokenSchema.safeParse(raw);
  if (!parsed.success) return false;
  const named = readEnvelope(parsed.data).eventId;
  rememberEventAccess(named ?? pathEventId, parsed.data);
  return true;
}

/* ----------------------------------------------------------- the gate, as this tab saw it */

/**
 * Whether this tab has seen the server answer as membership decides — an `event_access_required`
 * or an `eventsHidden` listing. Only then does a listing that carries a position also carry the
 * donor's session: SPEC.md §6.2 keeps a position apart from an identity, and with membership off
 * (live) the two are never joined, exactly as before GH-871.
 */
let gateSeen = false;

export const membershipGateSeen = (): boolean => gateSeen;
export const markMembershipGateSeen = (): void => {
  gateSeen = true;
};

/** Tests only: forget everything this module holds in memory. */
export function resetEventAccessForTests(): void {
  memory = {};
  storageRefused = false;
  gateSeen = false;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* nothing to clear */
  }
}
